import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { useMediaQuery } from "react-responsive";
import Cookies from "js-cookie";
import API_ENDPOINTS from "../config/api";

const ElectronicSignature2 = ({ onClose, onConfirm }) => {
  const navigate = useNavigate();
  const isDesktop = useMediaQuery({ minWidth: 768 });
  const [agreed, setAgreed] = useState(false);
  const [marketingAgreed, setMarketingAgreed] = useState(false);
  const [visible, setVisible] = useState(false);
  const [userName, setUserName] = useState("의뢰인");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [userData, setUserData] = useState(null);

  // 페이지 로드 시 모달이 하단에서 올라오도록 설정
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = Cookies.get(
          API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN
        );

        if (!token) {
          throw new Error("인증 토큰이 없습니다. 다시 로그인해주세요.");
        }

        const response = await fetch(`${API_ENDPOINTS.BASE_URL}/clients/me`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          throw new Error("사용자 정보를 가져오는데 실패했습니다.");
        }

        const data = await response.json();
        setUserData(data);
      } catch (err) {
        setError(err.message || "사용자 정보를 가져오는데 실패했습니다.");
      } finally {
        setVisible(true);
      }
    };

    fetchUserData();
  }, []);

  // 모달 닫기 처리
  const handleClose = () => {
    setVisible(false);
    setTimeout(() => {
      if (onClose) onClose();
    }, 300);
  };

  // 동의 처리
  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
  };

  const modalVariants = {
    hidden: {
      y: "100%",
      opacity: 1,
    },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        damping: 25,
        stiffness: 300,
      },
    },
    exit: {
      y: "100%",
      opacity: 1,
      transition: {
        duration: 0.3,
      },
    },
  };

  return (
    <ModalOverlay onClick={handleClose}>
      <AnimatePresence>
        {visible && (
          <ModalContainer
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader>
              <ModalTitle>개인정보 처리방침</ModalTitle>
              <CloseButton onClick={handleClose}>&times;</CloseButton>
            </ModalHeader>
            <ModalBody>
              <SectionContainer>
                <Section>
                  <SectionTitle
                    style={{
                      fontWeight: "bold",
                      fontSize: "1.2em",
                      marginBottom: "10px",
                    }}
                  >
                    개인정보처리방침_성난 유심 피해자들
                  </SectionTitle>
                  <SectionContent>
                    법무법인 더 에이치 황해 및 성난 유심 피해자들(이하 "성난
                    유심 피해자들")는 "개인정보 보호법", "전자상거래 등에서의
                    소비자보호에 관한 법률", "정보통신망 이용촉진 및 정보보호
                    등에 관한 법률" 등 관련 법령에 따라 이용자의 개인정보를
                    보호하고 개인정보와 관련한 이용자의 고충을 원활하게 처리할
                    수 있도록 개인정보처리방침에 따라 개인정보를 처리하고
                    있습니다.
                  </SectionContent>
                </Section>

                <Divider />

                <Section>
                  <SectionTitle>제1조 개인정보의 처리 목적</SectionTitle>
                  <SectionContent>
                    성난 유심 피해자들은 서비스 이용에 필수적인 개인정보를
                    다음과 같은 목적으로 처리합니다. 처리하는 개인정보는 다음의
                    목적 이외의 용도로는 이용되지 않으며, 처리 목적과 항목이
                    변경되는 경우에는 사전에 이용자의 동의를 받습니다.
                    <br />
                    <br />
                    1. <b>서비스 제공 및 요금 정산</b>
                    <br />- SKT 해킹 유출 손해배상 소제기 신청서 및 관련 서류
                    작성 및 신청 대행
                    <br />- 정기/비정기 이용료 자동 또는 수동 결제
                    <br />- 미지급 이용료, 비용에 대한 청구 및 법적 절차 진행
                    <br />
                    <br />
                    2. <b>회원 관리</b>
                    <br />- 본인 확인 및 회원 식별
                    <br />- 부정 이용 방지 및 비인가 사용 방지
                    <br />- 불만 및 민원처리, 고지사항 전달, 광고성 정보 제공 및
                    참여 기회 제공
                    <br />- 이용 기록 분석 및 통계 분석
                  </SectionContent>
                </Section>

                <Section>
                  <SectionTitle>
                    제2조 개인정보의 수집 및 처리 항목
                  </SectionTitle>
                  <SectionContent>
                    성난 유심 피해자들은 서비스 제공을 위해 아래와 같은 정보를
                    처리합니다.
                    <br />
                    <br />
                    1. IP 주소, 쿠키, 방문 일시, 서비스 이용 기록, 불량 이용
                    기록, 기기정보
                    <br />
                    2. 결제 서비스 이용 중 생성되는 정보
                    <br />
                    3. 고객 상담 중 생성되는 정보
                    <br />
                    <br />
                    <TableContainer>
                      <TableHeader>
                        <TableRow>
                          <TableCell>구분</TableCell>
                          <TableCell>동의구분</TableCell>
                          <TableCell>수집/처리항목</TableCell>
                          <TableCell>수집/처리목적</TableCell>
                          <TableCell>보유기간</TableCell>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <TableRow>
                          <TableCell>회원가입 시</TableCell>
                          <TableCell>필수</TableCell>
                          <TableCell>
                            ◦ 이름, ◦ 생년월일, ◦ 휴대전화번호, ◦ 개인식별정보
                          </TableCell>
                          <TableCell>
                            회원 식별 및 관리, 고객 상담 및 민원 처리, 광고성
                            정보 제공 등
                          </TableCell>
                          <TableCell>
                            회원 탈퇴 즉시 파기 (단, 관련 법령에 따라 보관 필요
                            시 예외)
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>SKT 손해배상 신청</TableCell>
                          <TableCell>필수</TableCell>
                          <TableCell>
                            ◦ 피해 정보, ◦ 통신사 정보, ◦ 신상 정보
                          </TableCell>
                          <TableCell>
                            소장 및 관련 서류 작성, 소제기 대리
                          </TableCell>
                          <TableCell>위임사무 최종 완료일로부터 1년</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>서비스 이용 시</TableCell>
                          <TableCell>자동 수집</TableCell>
                          <TableCell>
                            ◦ IP 주소, ◦ 방문 기록, ◦ 기기 정보, ◦ 서비스 이용
                            기록
                          </TableCell>
                          <TableCell>서비스 개선 및 이용 분석</TableCell>
                          <TableCell>6개월</TableCell>
                        </TableRow>
                      </TableBody>
                    </TableContainer>
                  </SectionContent>
                </Section>

                <Section>
                  <SectionTitle>제3조 개인정보 수집 방법</SectionTitle>
                  <SectionContent>
                    성난 유심 피해자들은 다음과 같은 방법으로 개인정보를
                    수집합니다.
                    <br />
                    <br />
                    1. 회원가입 및 서비스 이용 과정에서 이용자가 직접 입력한
                    정보
                    <br />
                    2. 상담 및 민원 처리 과정에서 생성되는 정보
                    <br />
                    3. 서비스 이용 과정에서 자동으로 수집되는 정보
                  </SectionContent>
                </Section>

                <Section>
                  <SectionTitle>
                    제4조 개인정보의 보유 및 이용 기간
                  </SectionTitle>
                  <SectionContent>
                    성난 유심 피해자들은 이용자가 동의한 개인정보 보유 기간
                    내에서 개인정보를 처리합니다. 회원이 탈퇴하거나 개인정보
                    수집·이용에 대한 동의를 철회하는 경우, 수집 목적이
                    달성되거나 보유기간이 종료한 경우에는 해당 정보를 지체 없이
                    파기합니다. 단, 법령에 따라 일정 기간 보관이 필요한 경우에는
                    다음과 같이 보관됩니다.
                    <br />
                    <br />
                    <TableContainer>
                      <TableHeader>
                        <TableRow>
                          <TableCell>법령</TableCell>
                          <TableCell>항목</TableCell>
                          <TableCell>보유기간</TableCell>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <TableRow>
                          <TableCell>전자상거래법</TableCell>
                          <TableCell>소비자 불만 또는 분쟁 처리 기록</TableCell>
                          <TableCell>3년</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>전자상거래법</TableCell>
                          <TableCell>계약 및 청약철회 기록</TableCell>
                          <TableCell>5년</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>전자상거래법</TableCell>
                          <TableCell>대금결제 및 공급 기록</TableCell>
                          <TableCell>5년</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>국세기본법</TableCell>
                          <TableCell>세법상 거래 기록</TableCell>
                          <TableCell>5년</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>전자금융거래법</TableCell>
                          <TableCell>전자금융 거래 기록</TableCell>
                          <TableCell>5년</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>표시·광고 공정화법</TableCell>
                          <TableCell>표시/광고에 대한 기록</TableCell>
                          <TableCell>6개월</TableCell>
                        </TableRow>
                      </TableBody>
                    </TableContainer>
                  </SectionContent>
                </Section>

                <Section>
                  <SectionTitle>제5조 개인정보의 위탁</SectionTitle>
                  <SectionContent>
                    성난 유심 피해자들은 원활한 서비스 제공을 위해 개인정보 처리
                    업무를 다음과 같이 위탁하고 있으며, 안전한 관리가 이루어질
                    수 있도록 필요한 조치를 취하고 있습니다.
                    <br />
                    <br />
                    <TableContainer>
                      <TableHeader>
                        <TableRow>
                          <TableCell>수탁업체</TableCell>
                          <TableCell>위탁업무 내용</TableCell>
                          <TableCell>보유 및 이용 기간</TableCell>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <TableRow>
                          <TableCell>법무법인 더에이치 황해</TableCell>
                          <TableCell>회원 정보의 처리 및 보관</TableCell>
                          <TableCell>위탁 계약 종료 시까지</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>법무법인 더에이치 황해</TableCell>
                          <TableCell>피해사실 증명서 발급</TableCell>
                          <TableCell>위탁 계약 종료 시까지</TableCell>
                        </TableRow>
                      </TableBody>
                    </TableContainer>
                  </SectionContent>
                </Section>

                <Section>
                  <SectionTitle>제6조 개인정보 제3자 제공</SectionTitle>
                  <SectionContent>
                    성난 유심 피해자들은 이용자의 개인정보를 원칙적으로 외부에
                    제공하지 않습니다. 다만, 법령에 따라 요청이 있거나 이용자의
                    별도 동의가 있는 경우 제공될 수 있습니다.
                  </SectionContent>
                </Section>

                <Section>
                  <SectionTitle>제7조 이용자의 권리와 의무</SectionTitle>
                  <SectionContent>
                    1. 이용자는 언제든지 본인의 개인정보를 조회, 수정 및 삭제할
                    수 있습니다.
                    <br />
                    2. 법정대리인이나 위임을 받은 자를 통해 권리를 행사할 수
                    있으며, 이 경우 위임장을 제출해야 합니다.
                    <br />
                    3. 만 14세 미만 아동의 개인정보는 수집하지 않습니다.
                    <br />
                    4. 이용자가 타인의 정보를 도용하거나 허위 정보를 입력하는
                    경우, 회원자격이 상실될 수 있으며 법적 처벌을 받을 수
                    있습니다.
                  </SectionContent>
                </Section>

                <Section>
                  <SectionTitle>제8조 개인정보 자동 수집 및 거부</SectionTitle>
                  <SectionContent>
                    1. 서비스 이용 과정에서 쿠키 및 기기 정보가 자동으로 수집될
                    수 있습니다.
                    <br />
                    2. 이용자는 브라우저 설정을 통해 쿠키를 차단할 수 있으나,
                    일부 서비스 이용이 제한될 수 있습니다.
                  </SectionContent>
                </Section>

                <Section>
                  <SectionTitle>제9조 개인정보의 파기</SectionTitle>
                  <SectionContent>
                    1. 성난 유심 피해자들은 개인정보 보유기간이 경과하거나
                    처리목적이 달성되었을 때에는 지체 없이 해당 개인정보를
                    파기합니다.
                    <br />
                    2. 개인정보 보유기간이 경과하거나 처리목적이 달성되었음에도
                    불구하고 다른 법령에 따라 개인정보를 계속 보존하여야 하는
                    경우에는, 해당 개인정보를 별도의 데이터베이스로 옮기거나
                    보관장소를 달리하여 보존합니다.
                    <br />
                    3. 개인정보 파기의 절차 및 방법은 다음과 같습니다.
                    <br />
                    &nbsp;&nbsp;a. 파기절차: 성난 유심 피해자들은 파기 사유가
                    발생한 개인정보를 선정하고 아래 방법에 따라 개인정보를
                    파기합니다.
                    <br />
                    &nbsp;&nbsp;b. 파기방법: 법령에 따라 전자적 파일 형태로
                    저장된 개인정보는 복구 또는 재생되지 아니하도록 파기하며,
                    종이 문서에 기록된 개인정보는 복구할 수 없도록 세절하는
                    방법으로 파기합니다.
                    <br />
                    &nbsp;&nbsp;c. 성난 유심 피해자들은 개인정보 보호법
                    제39조의6에 따라 장기 미이용자에 관한 정책을 다음과 같이
                    유지하고 있습니다.
                    <br />
                    &nbsp;&nbsp;d. 최근 1년 동안 이용 기록이 없는 회원(장기
                    미이용자)의 개인정보는 별도로 분리하여 안전하게 관리하게
                    되며, 대상자에게는 분리 보관 처리일을 기준으로 하여 최소
                    30일 이전에 이메일 주소를 통해 안내를 합니다. 단,
                    통신비밀보호법, 전자상거래 등에서의 소비자보호에 관한 법률
                    등의 관계법령의 규정에 의하여 보존할 필요가 있는 경우 규정된
                    기간 동안 이용자의 개인정보를 보관합니다.
                  </SectionContent>
                </Section>

                <Section>
                  <SectionTitle>제10조 개인정보 보호조치</SectionTitle>
                  <SectionContent>
                    성난 유심 피해자들은 개인정보 보호를 위해 다음과 같은 조치를
                    취합니다.
                    <br />
                    <br />
                    1. <b>관리적 조치</b>: 내부 관리계획 수립 및 직원 교육
                    <br />
                    2. <b>기술적 조치</b>: 접근권한 관리, 암호화 및 보안
                    프로그램 운영
                    <br />
                    3. <b>물리적 조치</b>: 서버 및 자료 보관소 접근 통제
                  </SectionContent>
                </Section>

                <Section>
                  <SectionTitle>제11조 개인정보 보호 책임자</SectionTitle>
                  <SectionContent>
                    회사는 귀하가 좋은 정보를 안전하게 이용할 수 있도록 최선을
                    다하고 있습니다.
                    <br />
                    <br />
                    이용자 개인정보와 관련한 아이디(ID)의 비밀번호에 대한
                    보안유지책임은 해당 이용자 자신에게 있습니다. 회사는
                    비밀번호에 대해 어떠한 방법으로도 이용자에게 직접적으로
                    질문하는 경우는 없으므로 타인에게 비밀번호가 유출되지 않도록
                    각별히 주의하시기 바랍니다. 특히 공공장소에서 온라인상에서
                    접속해 있을 경우에는 더욱 유의하셔야 합니다.
                    <br />
                    <br />
                    성난 유심 피해자들은 개인정보 보호 책임자를 다음과 같이
                    지정하고 있습니다.
                    <br />
                    <br />
                    <b>개인정보 보호 책임자</b>
                    <br />
                    이름: 천상현
                    <br />
                    전화: 032 - 251 - 1000
                    <br />
                    이메일: theh1000@lfthe-h.com
                    <br />
                    <br />
                    <b>개인정보 보호 담당자</b>
                    <br />
                    이름: 천상현
                    <br />
                    전화: 032 - 251 - 1000
                    <br />
                    이메일: theh1000@lfthe-h.com
                    <br />
                    <br />
                    기타 개인정보 침해 신고는 다음 기관에 문의할 수 있습니다.
                    <br />
                    <br />
                    - 개인정보침해 신고센터: 국번 없이 118 (privacy.kisa.or.kr)
                    <br />
                    - 개인정보 분쟁조정위원회: 1833-6972 (kopico.go.kr)
                    <br />
                    - 대검찰청 사이버수사과: 국번 없이 130 (spo.go.kr)
                    <br />- 경찰청 사이버수사국: 국번 없이 182
                    (ecrm.police.go.kr)
                  </SectionContent>
                </Section>

                <Section>
                  <SectionTitle>제12조 고지의 의무</SectionTitle>
                  <SectionContent>
                    본 개인정보 처리방침은 관련 법령 및 정책 변경에 따라 개정될
                    수 있으며, 변경 사항 발생 시 최소 7일 전에 공지합니다.
                    <br />
                    <br />
                    <b>부칙</b> 본 개인정보 처리방침은 2025년 4월 30일부터
                    적용됩니다.
                  </SectionContent>
                </Section>

                {/* <Divider /> */}

                {/* <Section>
                  <SectionTitle
                    style={{
                      fontWeight: "bold",
                      fontSize: "1.2em",
                      marginBottom: "10px",
                    }}
                  >
                    # &lt;마케팅 정보 수신 동의&gt;
                  </SectionTitle>
                  <SectionContent>
                    성난 유심 피해자들은 개인정보 보호법 등 관계 법령에 따라,
                    소송 안내 및 피해자 권익 보호와 관련된 홍보 목적(이하
                    "마케팅 목적")으로 개인정보를 수집 및 활용하기 위하여
                    수신자(귀하)의 사전 동의를 받고 있습니다.
                  </SectionContent>
                </Section> */}

                {/* <Divider /> */}

                {/* <Section>
                  <SectionTitle>제1조 (목적)</SectionTitle>
                  <SectionContent>
                    본 약관은 성난 유심 피해자들이 제공하는 서비스와 관련하여
                    마케팅 정보 수신에 대한 동의를 받기 위한 것입니다.
                  </SectionContent>
                </Section>

                <Section>
                  <SectionTitle>제2조 (수집 항목)</SectionTitle>
                  <SectionContent>
                    저희는 마케팅 정보 제공을 위해 다음과 같은 개인정보를
                    수집합니다.
                    <br />
                    <br />
                    - 이용자의 이름
                    <br />
                    - 이용자의 휴대전화 번호
                    <br />
                    - 이용자의 서비스 활동 로그
                    <br />- 마케팅 수신 동의 여부
                  </SectionContent>
                </Section>

                <Section>
                  <SectionTitle>제3조 (마케팅 정보 수신 동의)</SectionTitle>
                  <SectionContent>
                    이용자는 성난 유심 피해자들이 제공하는 소송 정보, 프로모션,
                    이벤트 등의 마케팅 자료를 수신하는 것에 대해 동의합니다.
                    이용자가 원하지 않을 경우 언제든지 수신 거부를 요청할 수
                    있습니다.
                  </SectionContent>
                </Section>

                <Section>
                  <SectionTitle>제4조 (수신 방법 및 변경 요청)</SectionTitle>
                  <SectionContent>
                    동의한 경우 다음과 같은 방법으로 마케팅 정보를 제공합니다.
                    <br />
                    <br />
                    - 푸시 알림
                    <br />
                    - 카카오 알림톡
                    <br />
                    - SMS
                    <br />
                    - 이메일
                    <br />
                    <br />
                    이용자가 수신 방법 변경 또는 수신 거부를 요청할 경우, 처리
                    완료까지 최대 48시간이 소요될 수 있습니다.
                  </SectionContent>
                </Section> */}

                {/* <Section>
                  <SectionTitle>제5조 (보유 및 이용 기간)</SectionTitle>
                  <SectionContent>
                    이용자가 마케팅 정보 수신 동의를 철회하거나 회원 탈퇴를 할
                    때까지 개인정보를 보유하고 이용합니다. 동의 철회 시, 해당
                    개인정보는 관련 법규에 따라 안전하게 삭제됩니다.
                  </SectionContent>
                </Section>

                <Section>
                  <SectionTitle>제6조 (개인정보 보호)</SectionTitle>
                  <SectionContent>
                    마케팅 수신에 따른 개인정보는 관련 법규에 따라 안전하게
                    관리됩니다.
                  </SectionContent>
                </Section>

                <Section>
                  <SectionTitle>제7조 (약관의 변경)</SectionTitle>
                  <SectionContent>
                    필요에 따라 본 약관을 변경할 수 있으며, 변경 시에는 사전에
                    고지합니다.
                  </SectionContent>
                </Section> */}

                {/* <Divider /> */}

                {/* <Section>
                  <SectionContent>
                    귀하는 위와 같이 마케팅을 위한 개인정보를 수집·이용하는 데
                    동의를 거부할 권리가 있습니다. 거부하실 경우에도 서비스
                    이용에는 제한이 없습니다.
                    <br />
                    <br />
                    단, 동의를 거부하실 경우 성난 유심 피해자들의 뉴스레터,
                    이벤트 운영과 혜택, 신규 정보에 대한 안내를 받지 못할 수
                    있습니다.
                  </SectionContent>
                </Section> */}

                <CheckboxContainer onClick={() => setAgreed(!agreed)}>
                  <CheckboxImage
                    src={
                      agreed ? "/png/checked.png" : "/png/checkedinActive.png"
                    }
                    alt="체크박스"
                  />
                  <CheckboxLabel>개인정보 처리방침에 동의합니다</CheckboxLabel>
                </CheckboxContainer>

                {/* <CheckboxContainer
                  onClick={() => setMarketingAgreed(!marketingAgreed)}
                >
                  <CheckboxImage
                    src={
                      marketingAgreed
                        ? "/png/checked.png"
                        : "/png/checkedinActive.png"
                    }
                    alt="체크박스"
                  />
                  <CheckboxLabel>
                    <MarketingLabel>[선택] 마케팅 정보 수신 동의</MarketingLabel>
                  </CheckboxLabel>
                </CheckboxContainer> */}
              </SectionContainer>
            </ModalBody>
            <ModalFooter>
              <ConfirmButton onClick={handleConfirm} disabled={!agreed}>
                동의하고 계속하기
              </ConfirmButton>
            </ModalFooter>
          </ModalContainer>
        )}
      </AnimatePresence>
    </ModalOverlay>
  );
};

// 스타일 컴포넌트
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 999;
  touch-action: none;

  @media (min-width: 769px) {
    align-items: center;
  }
`;

const ModalContainer = styled(motion.div)`
  width: 100%;
  max-width: 500px;
  background-color: white;
  border-radius: 16px 16px 0 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 80vh;

  @media (min-width: 769px) {
    border-radius: 16px;
    max-width: 420px;
    max-height: 90vh;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #eee;
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  flex: 1;
  text-align: center;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #999;
  padding: 0;
  margin: 0;
  cursor: pointer;
`;

const ModalBody = styled.div`
  padding: 20px;
  overflow-y: auto;
  flex: 1;
  font-size: 14px;
  line-height: 1.6;
`;

const ModalFooter = styled.div`
  padding: 15px 20px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: center;
`;

const ConfirmButton = styled.button`
  display: flex;
  width: 100%;
  height: 56px;
  padding: 0 14px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  background: ${(props) => (props.disabled ? "#E5E5E5" : "#0075ff")};
  color: ${(props) => (props.disabled ? "#999" : "white")};
  font-size: 16px;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "default" : "pointer")};
  border: none;
  opacity: ${(props) => (props.disabled ? 0.8 : 1)};
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};
`;

const SectionContainer = styled.div`
  margin-bottom: 20px;
`;

const Section = styled.div`
  margin-bottom: 25px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 10px;
  color: #333;
`;

const SectionContent = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: #444;
  white-space: pre-line;
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  margin: 15px 0;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
`;

const TableHeader = styled.div`
  background-color: #f5f5f5;
  font-weight: 600;
`;

const TableBody = styled.div``;

const TableRow = styled.div`
  display: flex;
  border-bottom: 1px solid #e0e0e0;

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.div`
  padding: 8px 10px;
  flex: 1;
  min-width: 80px;
  font-size: 12px;
  word-break: keep-all;
  display: flex;
  align-items: center;
  border-right: 1px solid #e0e0e0;

  &:last-child {
    border-right: none;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 20px;
  cursor: pointer;
`;

const CheckboxImage = styled.img`
  width: 24px;
  height: 24px;
  margin-right: 10px;
`;

const CheckboxLabel = styled.div`
  font-size: 15px;
  font-weight: 500;
`;

const MarketingLabel = styled.span`
  color: #777;
`;

const Divider = styled.div`
  height: 1px;
  background-color: #eee;
  margin: 20px 0;
`;

export default ElectronicSignature2;
